import { useCallback, useEffect, useRef, useState } from 'react';
import type { ServerPage } from '@/services/types/ServerPage';
import { useRequestSequence } from '@/services/useRequestSequence';

/**
 * What the server answered for `key`, asked again whenever `key` changes or `reload()` is called —
 * one page of a paged, filtered list, or anything else whose question changes while the screen is
 * open.
 *
 * Not useApiResource, and the two live side by side on purpose. That hook loads once on mount, has
 * no dependencies to re-run on, and deliberately lets a late response win and a failure propagate;
 * all of that is right for the pages it serves and exactly wrong for a list the reader pages,
 * searches and filters. Clicking through pages or retyping a search fires overlapping requests, and
 * the browser is free to answer page 2 after page 3: the sequence below is what makes the screen
 * show what was asked for last rather than whatever landed last.
 *
 * `key` is the question as a string — usually the query the request sends — because it is the
 * effect's dependency: a filter object rebuilt every render would restart the request forever, and
 * a string only changes when what is asked for does. `load` is held in a ref, as useApiResource
 * holds its fetcher, since callers pass an inline arrow with a fresh identity every render.
 *
 * `data` stays on screen while the next answer loads, so the layout does not bounce on every click;
 * `loading` is there for a caller that wants to dim it, and `answeredKey` says which question the
 * data on screen answers, for a caller that must not speak about the new question until its own
 * answer is in.
 *
 * A failed load leaves `data` alone and raises `failed`, with the rejection in `error`. Resolving
 * to an empty page instead would say "there is nothing here" about a list nobody managed to read —
 * a different and much worse sentence than "we could not load it". A `load` that throws on an
 * answer of the wrong shape fails the same way.
 *
 * `reload()` asks the same question again — after a mutation, so the page on screen is refreshed
 * rather than the reader thrown back to page one. It resolves once the answer is on screen (or the
 * failure is), for a caller that reports success only after the list shows it; it never rejects.
 */
export function useServerPage<T>(load: () => Promise<T>, key: string): ServerPage<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  const [answeredKey, setAnsweredKey] = useState<string | null>(null);
  // Bumped by `reload` to re-run the effect without changing what is asked for.
  const [reloads, setReloads] = useState(0);
  const begin = useRequestSequence();

  const loadRef = useRef(load);
  loadRef.current = load;
  // The reload() calls waiting for the next answer to reach the screen.
  const waiting = useRef<(() => void)[]>([]);

  useEffect(() => {
    const isLatest = begin();
    const settle = (apply: () => void) => {
      if (!isLatest()) return;
      apply();
      setAnsweredKey(key);
      setLoading(false);
      const settled = waiting.current;
      waiting.current = [];
      settled.forEach((resolve) => resolve());
    };
    setLoading(true);
    loadRef.current().then(
      (next) =>
        settle(() => {
          setData(next);
          setFailed(false);
          setError(undefined);
        }),
      (e: unknown) =>
        settle(() => {
          setFailed(true);
          setError(e);
        }),
    );
  }, [begin, key, reloads]);

  const reload = useCallback(
    () =>
      new Promise<void>((resolve) => {
        waiting.current.push(resolve);
        setReloads((n) => n + 1);
      }),
    [],
  );

  return { data, loading, failed, error, answeredKey, reload };
}
